// src/pages/ProjectDetailPage.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import InvestmentCalculator from '../components/common/InvestmentCalculator/InvestmentCalculator';
import PayPalCheckoutButton from '../components/common/PayPalCheckoutButton/PayPalCheckoutButton';
import TransferProofUploadModal from '../components/common/TransferProofUploadModal/TransferProofUploadModal';
import ErrorBoundary from '../components/common/ErrorBoundary/ErrorBoundary';
import styles from './ProjectDetailPage.module.css';
import { ProjectsService, InvestmentGate } from '../services/projects.service';
import type { Project } from '../types/Project';
import { FiAlertTriangle, FiLock } from 'react-icons/fi';
import { BlocksService, ProjectBlocksRules } from '../services/blocks.service';
import { PaymentMfaService } from '../services/payment-mfa.service';
import { useNotification } from '../context/NotificationContext';
import { resolveFileUrl } from '../utils/resolveFileUrl';
import { useAuth } from '../context/AuthContext';
import { useKycDocuments } from '../hooks/useKycDocuments';

interface ProjectDetailPageProps {
  publicView?: boolean;
}

const ProjectDetailPage: React.FC<ProjectDetailPageProps> = ({ publicView = false }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { addNotification } = useNotification();
  const { user } = useAuth();
  const { hasRequiredDocs, loading: kycDocsLoading } = useKycDocuments({
    enabled: !publicView && user?.role === 'client',
  });

  const [project, setProject] = useState<Project | null>(null);
  const [gate, setGate] = useState<InvestmentGate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [blocksRules, setBlocksRules] = useState<ProjectBlocksRules | null>(null);
  const [blocksQty, setBlocksQty] = useState<number>(0);
  const [blocksLoading, setBlocksLoading] = useState(false);

  const [mfaOpen, setMfaOpen] = useState(false);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaToken, setMfaToken] = useState<string>('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaTouched, setMfaTouched] = useState(false);
  const [mfaVerified, setMfaVerified] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const projectId = Number(id);
  const isSpanish = i18n.language.startsWith('es');
  const locale = isSpanish ? 'es-MX' : 'en-US';
  const currency: 'MXN' | 'USD' = isSpanish ? 'MXN' : 'USD';

  useEffect(() => {
    if (!Number.isFinite(projectId) || projectId <= 0) {
      setLoading(false);
      setProject(null);
      setGate(null);
      setError('ID de proyecto inválido');
      return;
    }

    let alive = true;
    setLoading(true);
    setError(null);

    ProjectsService.getById(projectId)
      .then(async project => {
        if (!alive) return;

        if (!project) {
          throw new Error('Proyecto no encontrado');
        }

        setProject(project);

        if (publicView) {
          setGate(null);
          return;
        }

        try {
          const g = await ProjectsService.getInvestmentGate(projectId);
          if (!alive) return;
          setGate(g || null);
        } catch (error) {
          console.warn('Investment gate endpoint not available:', error);
          if (!alive) return;
          setGate({
            canInvest: true,
            kycRequired: false,
            pldRequired: false
          });
        }
      })
      .catch(err => {
        if (!alive) return;
        console.error('Error loading project:', err);
        const rawMessage = err instanceof Error ? err.message : '';
        const isHiddenDraft = /Development not found|Proyecto no encontrado|not found/i.test(rawMessage);
        setProject(null);
        setGate(null);
        setError(
          isHiddenDraft && user?.role === 'developer'
            ? 'Este proyecto todavía no está disponible como vista pública. Envía todos los documentos solicitados y espera la aprobación del administrador para verlo publicado.'
            : (rawMessage || 'Error al cargar el proyecto'),
        );
      })
      .finally(() => {
        if (!alive) return;
        setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [projectId, publicView, user?.role]);

  useEffect(() => {
    if (publicView) {
      setBlocksRules(null);
      setBlocksQty(0);
      setBlocksLoading(false);
      return;
    }

    let alive = true;

    const loadRules = async () => {
      if (!Number.isFinite(projectId) || projectId <= 0) return;

      setBlocksLoading(true);
      try {
        const rules = await BlocksService.getRules(String(projectId));
        if (!alive) return;

        setBlocksRules(rules || null);

        if ((rules as any)?.minBlocks) setBlocksQty(Number((rules as any).minBlocks));
        else setBlocksQty(0);
      } catch (error) {
        if (!alive) return;
        if (error instanceof Error && !error.message.includes('404')) {
          console.warn('Error loading blocks rules:', error);
        }
        setBlocksRules(null);
        setBlocksQty(0);
      } finally {
        if (!alive) return;
        setBlocksLoading(false);
      }
    };

    loadRules();

    return () => {
      alive = false;
    };
  }, [projectId, publicView]);

  const currentInvoiceId = useMemo(() => {
    if (!project) return '';
    return `INV-${project.id}-${Date.now()}`;
  }, [project]);

  const formatCurrency = (amount: number) =>
    Number(amount || 0).toLocaleString(locale, { style: 'currency', currency, minimumFractionDigits: 0 });

  const formatDate = (value?: string | Date | null) => {
    if (!value) return '—';
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(locale);
  };

  const docsGateEnabled = !publicView && user?.role === 'client';
  const docsGateBlocked = docsGateEnabled && (kycDocsLoading || !hasRequiredDocs);
  const blockedByGate = !publicView && gate ? !gate.canInvest : false;
  const blocked = blockedByGate || docsGateBlocked;
  const blockedReasons = [
    ...(docsGateBlocked
      ? [kycDocsLoading ? 'Verificando documentos KYC...' : 'Debes subir tus documentos KYC para poder invertir.']
      : []),
    ...(blockedByGate && gate?.reasons && gate.reasons.length > 0 ? gate.reasons : []),
  ];
  const rulesMinBlocks = Number((blocksRules as any)?.minBlocks ?? 0);
  const rulesMaxBlocks = (blocksRules as any)?.maxBlocks;
  const rulesBlockPrice = Number((blocksRules as any)?.blockPrice ?? 0);

  const fallbackMinBlocks = project?.minBlocks != null ? Number(project.minBlocks) : 0;
  const fallbackMaxBlocks = project?.maxBlocks != null ? Number(project.maxBlocks) : null;
  const fallbackBlockPrice = project?.blockPrice != null ? Number(project.blockPrice) : 0;

  const minBlocksValue =
    Number.isFinite(rulesMinBlocks) && rulesMinBlocks > 0
      ? rulesMinBlocks
      : Number.isFinite(fallbackMinBlocks) && fallbackMinBlocks > 0
        ? fallbackMinBlocks
        : null;

  const maxBlocksValue =
    typeof rulesMaxBlocks === 'number' && Number.isFinite(rulesMaxBlocks) && rulesMaxBlocks > 0
      ? rulesMaxBlocks
      : typeof fallbackMaxBlocks === 'number' && Number.isFinite(fallbackMaxBlocks) && fallbackMaxBlocks > 0
        ? fallbackMaxBlocks
        : null;

  const blockPrice =
    Number.isFinite(rulesBlockPrice) && rulesBlockPrice > 0
      ? rulesBlockPrice
      : Number.isFinite(fallbackBlockPrice) && fallbackBlockPrice > 0
        ? fallbackBlockPrice
        : 0;
  const investmentMinAmount =
    minBlocksValue && blockPrice ? minBlocksValue * blockPrice : null;
  const resolvedBlocksQtyMin = minBlocksValue ?? 0;
  const resolvedBlocksQtyMax = maxBlocksValue ?? undefined;

  const investmentAmount = useMemo(() => {
    if (blocksRules || blockPrice > 0) {
      const qty = Number(blocksQty || 0);
      const price = Number(blockPrice || 0);
      return qty > 0 ? qty * price : 0;
    }
    return 10000;
  }, [blocksRules, blockPrice, blocksQty]);

  const blocksQtyValid = useMemo(() => {
    if (!blocksRules && !blockPrice) return true;
    const qty = Number(blocksQty || 0);
    if (!Number.isFinite(qty) || qty <= 0) return false;
    if (resolvedBlocksQtyMin && qty < resolvedBlocksQtyMin) return false;
    if (typeof resolvedBlocksQtyMax === 'number' && qty > resolvedBlocksQtyMax) return false;
    return true;
  }, [blocksRules, blockPrice, blocksQty, resolvedBlocksQtyMin, resolvedBlocksQtyMax]);
  const fundingGoalValue =
    project && project.fundingGoal !== null && project.fundingGoal !== undefined
      ? Number(project.fundingGoal)
      : null;
  const fundingGoalLabel = Number.isFinite(fundingGoalValue || NaN)
    ? formatCurrency(fundingGoalValue as number)
    : '—';
  const progressValue = Number.isFinite(Number(project?.fundingProgress))
    ? Number(project?.fundingProgress)
    : 0;

  const paypalUiDisabled =
    blocked ||
    !gate ||
    (blocksRules ? !blocksQtyValid : false) ||
    (blocksRules ? investmentAmount <= 0 : false);
  const canInvestActions = user?.role === 'client';

  useEffect(() => {
    if (minBlocksValue && (!Number.isFinite(blocksQty) || blocksQty < minBlocksValue)) {
      setBlocksQty(minBlocksValue);
    }
  }, [minBlocksValue, blocksQty]);

  const resetMfaState = () => {
    setMfaOpen(false);
    setMfaLoading(false);
    setMfaToken('');
    setMfaCode('');
    setMfaTouched(false);
    setMfaVerified(false);
  };

  const openMfaBeforePayPal = async () => {
    if (mfaVerified) return;

    if (paypalUiDisabled) {
      addNotification('Revisa requisitos de inversión antes de continuar.', 'info');
      return;
    }

    if (!project) return;

    setMfaOpen(true);
    setMfaLoading(true);
    setMfaTouched(false);
    setMfaCode('');

    try {
      const res = await PaymentMfaService.request({
        projectId: project.id,
        invoiceId: currentInvoiceId,
        amount: investmentAmount,
        currency,
        provider: 'paypal'
      });

      if (!res?.mfaToken) throw new Error('No se recibió mfaToken');
      setMfaToken(res.mfaToken);
      addNotification('Te enviamos un código a tu correo.', 'info');
    } catch (e: any) {
      console.error(e);
      const errorMessage = e?.response?.data?.message || e?.message || 
        'No se pudo enviar el código de verificación.';
      
      if (errorMessage.includes('bloqueada') || errorMessage.includes('locked')) {
        addNotification(errorMessage, 'error');
      } else {
        addNotification(
          'No se pudo enviar el código de verificación. Revisa el backend/endpoints de MFA de pagos.',
          'error'
        );
      }
      setMfaOpen(false);
    } finally {
      setMfaLoading(false);
    }
  };

  const verifyMfaAndEnablePayPal = async () => {
    setMfaTouched(true);
    const code = mfaCode.trim();
    if (code.length < 6) return;

    if (!mfaToken) {
      addNotification('No hay verificación pendiente. Intenta de nuevo.', 'error');
      return;
    }

    setMfaLoading(true);
    try {
      await PaymentMfaService.verify(mfaToken, code);
      setMfaVerified(true);
      setMfaOpen(false);
      addNotification('Verificación exitosa. Ya puedes pagar con PayPal.', 'success');
    } catch (e: any) {
      console.error(e);
      
      // Manejar diferentes tipos de errores
      const errorMessage = e?.response?.data?.message || e?.message || 'Código inválido o expirado.';
      
      if (errorMessage.includes('bloqueada') || errorMessage.includes('locked')) {
        addNotification(errorMessage, 'error');
        setMfaOpen(false);
        resetMfaState();
      } else if (errorMessage.includes('Intentos restantes')) {
        addNotification(errorMessage, 'info');
      } else {
        addNotification('Código inválido o expirado.', 'error');
      }
    } finally {
      setMfaLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.detailPage}>
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <div>{t('common.loading') || 'Cargando...'}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.detailPage}>
        <div
          style={{
            padding: '2rem',
            textAlign: 'center',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            backgroundColor: '#fff',
            margin: '2rem 0'
          }}
        >
          <h2 style={{ color: '#d32f2f', marginBottom: '1rem' }}>
            {t('errors.project_error_title') || 'Error al cargar el proyecto'}
          </h2>
          <p style={{ marginBottom: '1.5rem', color: '#666' }}>{error}</p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {t('errors.try_again') || 'Intentar de nuevo'}
            </button>
            <button
              onClick={() => navigate('/proyectos')}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#666',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {t('common.go_back') || 'Volver a proyectos'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!project) return <Navigate to="/proyectos" replace />;

  const toYoutubeEmbed = (url?: string) => {
    if (!url) return '';
    const trimmed = url.trim();
    if (!trimmed) return '';
    if (trimmed.includes('youtube.com/embed/')) return trimmed;
    const match = trimmed.match(/(?:v=|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
    if (match?.[1]) return `https://www.youtube.com/embed/${match[1]}`;
    return trimmed;
  };

  const videoUrl = toYoutubeEmbed(project.videoUrl);

  const galleryImages = (project?.gallery || [])
    .filter(Boolean)
    .filter(img => img && typeof img === 'string');
  const gallerySlots = Array.from({ length: 3 }, (_, idx) => galleryImages[idx] || null);
  const fallbackGalleryImage = '/Profunding_FondoDegradado.png';

  const translatedDescription =
    t('projects.default_long_description', {
      title: project?.title || '',
      location: project?.location || ''
    }) || project?.description || '';

  const translatedDescriptionSecondary =
    t('projects.default_secondary_description') || 'Inversión destinada a remodelación de espacios de lujo.';

  return (
    <ErrorBoundary>
      <div className={styles.detailPage}>
        <h1 className={styles.title}>
          {t('projects.detail_title')}: {project?.title || ''}
        </h1>
        <p className={styles.location}>{project?.location || ''}</p>

        <div className={`${styles.contentGrid} ${publicView ? styles.contentGridPublic : ''}`}>
          <div className={styles.mainContent}>
            <div className={styles.mediaSection}>
              <h2 className={styles.sectionTitle}>{t('projects.detail_media_title')}</h2>

              {videoUrl ? (
                <div className={styles.videoContainer}>
                  <iframe
                    src={videoUrl}
                    title={t('projects.video_title') || 'Video del Proyecto'}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className={styles.videoFrame}
                  />
                </div>
              ) : null}

              <div className={styles.imageGrid}>
                {gallerySlots.map((img, index) =>
                  img ? (
                    <button
                      key={`${img}-${index}`}
                      type="button"
                      className={styles.galleryButton}
                      onClick={() => setPreviewImage(String(img))}
                      aria-label={t('projects.image_alt', { index: index + 1 }) || `Vista del proyecto ${index + 1}`}
                    >
                      <img
                        src={img as string}
                        alt={t('projects.image_alt', { index: index + 1 }) || `Vista del proyecto ${index + 1}`}
                        className={styles.galleryImage}
                        onError={e => {
                          if (e.currentTarget.dataset.fallback === 'true') return;
                          e.currentTarget.dataset.fallback = 'true';
                          e.currentTarget.src = fallbackGalleryImage;
                        }}
                      />
                    </button>
                  ) : (
                    <div key={`placeholder-${index}`} className={styles.galleryPlaceholder}>
                      Sin imagen
                    </div>
                  )
                )}
              </div>
            </div>

            <div className={styles.textSection}>
              <h2>{t('projects.detail_description_title')}</h2>
              <p>{translatedDescription}</p>
              <p>{translatedDescriptionSecondary}</p>
            </div>

            {project.documents && project.documents.length > 0 ? (
              <div className={styles.textSection}>
                <h2>{t('projects.documents_title') || 'Documentos'}</h2>
                <ul className={styles.financialList}>
                  {project.documents.map((doc, idx) => (
                    <li key={`${doc.file_url}-${idx}`}>
                      <a href={resolveFileUrl(doc.file_url)} target="_blank" rel="noreferrer">
                        {doc.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div className={styles.financialSection}>
              <h2>{t('projects.detail_financial_title')}</h2>
              <ul className={styles.financialList}>
                <li>
                  <span>{t('projects.investment_dates')}:</span>
                  <strong>
                    {formatDate(project.investmentStart)} — {formatDate(project.investmentEnd)}
                  </strong>
                </li>
                <li>
                  <span>{t('projects.funding_goal')}:</span>
                  <strong>{fundingGoalLabel}</strong>
                </li>
                <li>
                  <span>{t('projects.funding_progress')}:</span>
                  <strong>{progressValue}%</strong>
                </li>
                <li>
                  <span>{t('projects.term_months')}:</span>
                  <strong>{project.termMonths ? `${project.termMonths} ${t('projects.months')}` : '—'}</strong>
                </li>
                <li>
                  <span>{t('projects.block_price')}:</span>
                  <strong>{blockPrice ? formatCurrency(blockPrice) : '—'}</strong>
                </li>
                <li>
                  <span>{t('projects.block_rules')}:</span>
                  <strong>
                    {minBlocksValue || maxBlocksValue
                      ? `${minBlocksValue ?? '—'} - ${maxBlocksValue ?? '—'}`
                      : '—'}
                  </strong>
                </li>
                <li>
                  <span>{t('projects.min_investment')}:</span>
                  <strong>{investmentMinAmount ? formatCurrency(investmentMinAmount) : '—'}</strong>
                </li>
              </ul>
            </div>
          </div>

          {!publicView ? (
            <aside className={styles.sidebar}>
              {blocked ? (
              <div
                style={{
                  border: '1px solid rgba(0,0,0,0.08)',
                  background: 'white',
                  borderRadius: 12,
                  padding: 12,
                  display: 'grid',
                  gap: 10
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <FiLock />
                  <div style={{ fontWeight: 800 }}>{t('investments.blocked_title') || 'Acción restringida'}</div>
                </div>

                <div style={{ opacity: 0.9 }}>
                  {blockedReasons.length > 0
                    ? blockedReasons.join(' • ')
                    : t('investments.blocked_kyc_pld') || 'Completa KYC/PLD para operar.'}
                </div>

                <button
                  type="button"
                  onClick={() => navigate('/perfil')}
                  style={{
                    padding: '10px 12px',
                    borderRadius: 10,
                    border: '1px solid rgba(0,0,0,0.12)',
                    background: 'white',
                    cursor: 'pointer',
                    fontWeight: 800
                  }}
                >
                  {t('investments.go_to_profile') || 'Ir a Perfil (KYC/PLD)'}
                </button>
              </div>
            ) : (
              <>
                <InvestmentCalculator
                  blockPrice={blockPrice || undefined}
                  minBlocks={minBlocksValue}
                  maxBlocks={maxBlocksValue ?? undefined}
                  termMonths={project.termMonths ?? null}
                  investmentEndAt={project.investmentEnd}
                />

                {blocksRules || blockPrice > 0 || minBlocksValue || maxBlocksValue ? (
                  <div
                    style={{
                      border: '1px solid rgba(0,0,0,0.08)',
                      background: 'white',
                      borderRadius: 12,
                      padding: 12,
                      display: 'grid',
                      gap: 10,
                      marginTop: 12
                    }}
                    >
                    <div style={{ fontWeight: 900 }}>{t('blocks.title') || 'Compra de bloques'}</div>

                    <div style={{ opacity: 0.9, fontWeight: 700 }}>
                      {(t('blocks.price_per_block') || 'Precio por bloque')}:{' '}
                      {blockPrice > 0 ? formatCurrency(blockPrice) : '—'}
                    </div>

                    <label style={{ display: 'grid', gap: 6, fontWeight: 800 }}>
                      <span>{t('blocks.quantity') || 'Cantidad de bloques'}</span>
                      <input
                        type="number"
                        min={resolvedBlocksQtyMin || undefined}
                        max={typeof resolvedBlocksQtyMax === 'number' ? resolvedBlocksQtyMax : undefined}
                        value={blocksQty}
                        onChange={e => {
                          setBlocksQty(Number(e.target.value));
                          resetMfaState();
                        }}
                        style={{
                          padding: '10px 12px',
                          borderRadius: 10,
                          border: '1px solid rgba(0,0,0,0.12)',
                          fontWeight: 800
                        }}
                      />
                      <span style={{ fontSize: 12, opacity: 0.8 }}>
                        {(t('blocks.min_max') || 'Mín: {{min}} | Máx: {{max}}')
                          .replace('{{min}}', String(resolvedBlocksQtyMin))
                          .replace('{{max}}', String(typeof resolvedBlocksQtyMax === 'number' ? resolvedBlocksQtyMax : '—'))}
                      </span>
                    </label>

                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 900 }}>
                      <span>{t('blocks.total') || 'Total'}</span>
                      <span>{formatCurrency(investmentAmount)}</span>
                    </div>
                  </div>
                ) : blocksLoading ? (
                  <div style={{ marginTop: 12, opacity: 0.85, fontWeight: 800 }}>{t('common.loading') || 'Cargando...'}</div>
                ) : null}

                <div style={{ marginTop: 12 }}>
                  {canInvestActions ? (
                    <>
                      {!mfaVerified ? (
                        <button
                          type="button"
                          onClick={openMfaBeforePayPal}
                          disabled={paypalUiDisabled}
                          style={{
                            width: '100%',
                            border: 'none',
                            borderRadius: 16,
                            padding: '12px 16px',
                            background: paypalUiDisabled ? 'rgba(103,58,183,0.25)' : '#673ab7',
                            color: '#fff',
                            fontWeight: 900,
                            cursor: paypalUiDisabled ? 'not-allowed' : 'pointer',
                            boxShadow: 'var(--sombra-elevacion-1)'
                          }}
                        >
                          Continuar con PayPal
                        </button>
                      ) : (
                        <PayPalCheckoutButton
                          amount={investmentAmount}
                          description={project.title}
                          invoiceId={currentInvoiceId}
                          currency={currency}
                          disabled={paypalUiDisabled}
                          onSuccess={async ({ orderID }) => {
                            try {
                              if (blocksRules) {
                                await BlocksService.createInvestment({
                                  projectId: project.id,
                                  blocks: blocksQty,
                                  provider: 'paypal',
                                  invoiceId: currentInvoiceId,
                                  orderId: orderID,
                                  currency
                                });
                              } else {
                                await ProjectsService.createInvestment({
                                  projectId: project.id,
                                  amount: investmentAmount,
                                  currency,
                                  invoiceId: currentInvoiceId,
                                  provider: 'paypal'
                                });
                              }
                            } finally {
                              navigate(
                                `/mis-inversiones?status=success&invoice=${encodeURIComponent(
                                  currentInvoiceId
                                )}&order=${encodeURIComponent(orderID)}`
                              );
                            }
                          }}
                          onError={() => {}}
                        />
                      )}

                      <button
                        type="button"
                        onClick={() => setShowTransferModal(true)}
                        disabled={paypalUiDisabled}
                        style={{
                          width: '100%',
                          border: '1px solid #673ab7',
                          borderRadius: 16,
                          padding: '12px 16px',
                          background: '#fff',
                          color: '#673ab7',
                          fontWeight: 900,
                          cursor: paypalUiDisabled ? 'not-allowed' : 'pointer',
                          marginTop: 8,
                          opacity: paypalUiDisabled ? 0.5 : 1,
                          transition: 'all 0.2s',
                        }}
                        onMouseOver={(e) => {
                          if (!paypalUiDisabled) {
                            e.currentTarget.style.backgroundColor = '#f3e5f5';
                          }
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.backgroundColor = '#fff';
                        }}
                      >
                        📋 Subir Comprobante de Transferencia
                      </button>
                    </>
                  ) : (
                    <div
                      style={{
                        border: '1px dashed rgba(0,0,0,0.2)',
                        borderRadius: 14,
                        padding: 12,
                        fontWeight: 800,
                        color: '#475569',
                        background: '#f8fafc'
                      }}
                    >
                      Las acciones de inversión solo están disponibles para inversionistas.
                    </div>
                  )}
                </div>

                {!gate ? (
                  <div style={{ marginTop: 10, opacity: 0.85, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FiAlertTriangle />
                    <span>{t('investments.gate_unavailable') || 'Validación KYC/PLD no disponible.'}</span>
                  </div>
                ) : null}

                {blocksRules && !blocksQtyValid ? (
                  <div style={{ marginTop: 10, opacity: 0.9, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FiAlertTriangle />
                    <span>{t('blocks.invalid_qty') || 'Revisa la cantidad de bloques.'}</span>
                  </div>
                ) : null}
              </>
            )}
            </aside>
          ) : null}
        </div>

        {!publicView && mfaOpen ? (
          <div
            role="dialog"
            aria-modal="true"
            onClick={() => !mfaLoading && setMfaOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.55)',
              display: 'grid',
              placeItems: 'center',
              padding: 16,
              zIndex: 2500
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                width: '100%',
                maxWidth: 420,
                background: '#fff',
                border: '1px solid #eee',
                borderRadius: 18,
                padding: 18,
                boxShadow: '0 12px 30px rgba(0,0,0,0.18)'
              }}
            >
              <div style={{ marginBottom: 10 }}>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 900 }}>Verificación por correo</h2>
                <p style={{ margin: '8px 0 0', color: '#666', fontWeight: 600 }}>
                  Te enviamos un código a tu correo. Escríbelo para continuar con PayPal.
                </p>
              </div>

              <div style={{ marginTop: 14, display: 'grid', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', fontWeight: 900, marginBottom: 6 }}>Código</label>
                  <input
                    value={mfaCode}
                    onChange={e => setMfaCode(e.target.value.replace(/\s/g, ''))}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="123456"
                    disabled={mfaLoading}
                    style={{
                      width: '100%',
                      borderRadius: 12,
                      border: mfaTouched && mfaCode.trim().length < 6 ? '1px solid #ef4444' : '1px solid #e5e7eb',
                      padding: 12,
                      outline: 'none',
                      fontWeight: 900,
                      letterSpacing: 2,
                      opacity: mfaLoading ? 0.8 : 1
                    }}
                  />
                  {mfaTouched && mfaCode.trim().length < 6 ? (
                    <div style={{ marginTop: 8, color: '#b91c1c', fontWeight: 800, fontSize: 12 }}>
                      Ingresa un código válido (mínimo 6 dígitos).
                    </div>
                  ) : null}
                </div>

                <button
                  type="button"
                  onClick={verifyMfaAndEnablePayPal}
                  disabled={mfaLoading || mfaCode.trim().length < 6}
                  style={{
                    border: 'none',
                    borderRadius: 12,
                    padding: '12px 14px',
                    background: '#673ab7',
                    color: '#fff',
                    fontWeight: 900,
                    cursor: 'pointer',
                    opacity: mfaLoading || mfaCode.trim().length < 6 ? 0.7 : 1
                  }}
                >
                  {mfaLoading ? 'Validando...' : 'Continuar'}
                </button>

                <button
                  type="button"
                  onClick={() => !mfaLoading && setMfaOpen(false)}
                  disabled={mfaLoading}
                  style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: 12,
                    padding: '12px 14px',
                    background: '#fff',
                    color: '#111827',
                    fontWeight: 900,
                    cursor: 'pointer',
                    opacity: mfaLoading ? 0.7 : 1
                  }}
                >
                  Cancelar
                </button>

                <button
                  type="button"
                  onClick={() => !mfaLoading && openMfaBeforePayPal()}
                  disabled={mfaLoading}
                  style={{
                    border: '1px dashed #e5e7eb',
                    borderRadius: 12,
                    padding: '12px 14px',
                    background: '#fff',
                    color: '#111827',
                    fontWeight: 900,
                    cursor: 'pointer',
                    opacity: mfaLoading ? 0.7 : 1
                  }}
                >
                  Reenviar código
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {previewImage ? (
          <div
            className={styles.imageModal}
            role="dialog"
            aria-modal="true"
            onClick={() => setPreviewImage(null)}
          >
            <div
              className={styles.imageModalContent}
              onClick={e => e.stopPropagation()}
            >
              <button
                type="button"
                className={styles.imageModalClose}
                onClick={() => setPreviewImage(null)}
                aria-label="Cerrar vista previa"
              >
                ×
              </button>
              <img
                src={previewImage}
                alt="Vista previa"
                className={styles.imageModalImage}
              />
            </div>
          </div>
        ) : null}

        {/* Modal de carga de comprobante de transferencia */}
        {!publicView && showTransferModal && project && (
          <TransferProofUploadModal
            isOpen={showTransferModal}
            onClose={() => setShowTransferModal(false)}
            developmentId={project.id}
            developmentName={project.title}
            amount={investmentAmount}
            currency={currency}
          />
        )}
      </div>
    </ErrorBoundary>
  );
};

export default ProjectDetailPage;
